import { OrderModel } from "../../../shared/models/order/order-item.model";
import { TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { OrderListContainer, OrderTable, OrderTableCell, OrderTableRow } from "./styled";
import React, { useEffect, useState } from "react";
import Message from "../../../components/message";
import { MessageSections } from "../../../components/message/message";
import BarekamCheckbox from "../../../components/checkbox";
import { RowCell } from "./cell/row-cell";
import { OrdersResponseModel } from "../../../shared/models/api/orders-response.model";
import { AllOrdersResponseModel } from "../../../shared/models/api/all-orders-response.model";
import BarekamText from "../../../components/text";
import { ToolPanel } from "../tool-panel/tool-panel";
import NoOrders from "../no-orders";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import BarekamPagination from "../pagination/barekam-pagination";
import { changePagination, getOrders } from "../../../../redux/slices/orders-slice";
import { OrdersTableColumn } from "../models/column-models";
import {
    BulkActionPopupActionTypes,
    InWarehouseActionTypes,
    ReadyForShipmentActionTypes
} from "../../../../management-pages/actions/action-types.enum";
import { useOutletContext } from "react-router-dom";
import { OutletContext } from "../../../../router/router";
import orderGroupService from "../../../../services/order-group.service";
import { OrderGroupModel } from "../../../shared/models/order/orderGroupModel";
import PayConfirmDialog from "./pay-confirm-dialog";

const OrderList = (props: OrderListProps) => {
    const { isLoading, orderStateId } = useAppSelector(state => state.ordersReducer);

    const { ordersData, columns, openAction, onAction, isWarehouseWorker, setPaymentPopupOpened } = props;
    const [selectedOrders, setSelectedOrders] = useState(new Map<number, OrderModel>());
    const [groups, setGroups] = useState([] as OrderGroupModel[]);
    const [dialogOpen, setDialogState] = useState<boolean>(false);
    const [orderToPay, setOrderToPay] = useState<OrderModel>();

    const outletContext = useOutletContext<OutletContext>();

    const dispatch = useAppDispatch();

    useEffect(() => {
        setSelectedOrders(prevState => {
            prevState.clear();
            return prevState;
        });
    }, [orderStateId])

    useEffect(() => {
        if (ordersData && ordersData.orders.length && outletContext.isWarehouseWorker) {
            const ordersIds = ordersData.orders.map(order => order.id);
            orderGroupService.getGroups({ orders: ordersIds, orderStateId }, 0, 10000)
                .then((result) => {
                    setGroups(result.groups)
                });
        }
    }, [ordersData])

    function handlePageChange(event: any, newPage: number): void {
        dispatch(changePagination({ pageSize: ordersData.pageSize, currentPage: newPage - 1 }));
        dispatch(getOrders({ page: newPage - 1 }));
    }

    function handleSelect(isChecked: boolean, order: OrderModel): void {
        if (isChecked) {
            setSelectedOrders(selectedOrders => {
                if (order.orderId) {
                    selectedOrders.set(order.orderId, order);
                }
                return new Map(selectedOrders);
            });
        } else {
            setSelectedOrders(selectedOrders => {
                if (order.orderId) {
                    selectedOrders.delete(order.orderId);
                }
                return new Map(selectedOrders);
            });
        }
    }

    function handleSelectAllClick(isChecked: boolean): void {
        if (isChecked) {
            setSelectedOrders(selectedOrders => {
                ordersData.orders.forEach(order => order.orderId && selectedOrders.set(order.orderId, order));
                return new Map(selectedOrders);
            });
        } else {
            setSelectedOrders(new Map());
        }
    }

    const OrderListTableHead = (props: any) => {
        const { onSelectAllClick, rowCount } = props;

        return (
            <TableHead>
                <TableRow>
                    <TableCell padding="checkbox">
                        <BarekamCheckbox
                            color="primary"
                            indeterminate={selectedOrders.size !== rowCount && !!selectedOrders.size}
                            checked={rowCount > 0 && selectedOrders.size === rowCount}
                            onChange={(event) => onSelectAllClick(event.target.checked)}
                        />
                    </TableCell>
                    {columns.map((column, index) => (
                        <OrderTableCell key={`${index}-${column.field}`}>
                            <BarekamText variant={'m'} align={index === columns.length - 1 ? 'right' : 'left'}>
                                <Message messageKey={column.headerName} sectionId={MessageSections.ORDERS} />
                            </BarekamText>
                        </OrderTableCell>
                    ))}
                </TableRow>
            </TableHead>
        )
    }

    return (
        <>
            <ToolPanel selectedOrders={selectedOrders} isWarehouseWorker={isWarehouseWorker} orderStateId={orderStateId} />
            {dialogOpen && orderToPay && (
                <PayConfirmDialog open={dialogOpen} setOpen={setDialogState} setPaymentPopupOpened={setPaymentPopupOpened} order={orderToPay} />
            )}
            <OrderListContainer>
                {ordersData.orders.length
                    ? <OrderTable width={'100%'}>
                        <OrderListTableHead onSelectAllClick={handleSelectAllClick}
                            numSelected={0}
                            rowCount={ordersData.orders.length} />

                        <TableBody>
                            {ordersData.orders.map((order, index) => {
                                return (
                                    <OrderTableRow
                                        hover
                                        tabIndex={index}
                                        key={`${index}-${order.orderId}`}>
                                        <TableCell padding="checkbox">
                                            <BarekamCheckbox
                                                color="primary"
                                                checked={!!order.orderId && selectedOrders.has(order.orderId)}
                                                onChange={(event) => handleSelect(event.target.checked, order)}
                                            />
                                        </TableCell>

                                        {columns.map((column, columnIndex) => {
                                            return (
                                                <RowCell cellVariant={column.variant}
                                                    field={column.field}
                                                    row={order}
                                                    groups={groups}
                                                    isName={columnIndex === 0}
                                                    key={`${columnIndex}-${order.orderId}-${column.field}`}
                                                    clicked={openAction}
                                                    onAction={onAction}
                                                    payButtonClicked={() => { setDialogState(true), setOrderToPay(order) }}
                                                    isWarehouseWorker={outletContext.isWarehouseWorker}
                                                />
                                            )
                                        })}
                                    </OrderTableRow>
                                );
                            })}
                        </TableBody>
                    </OrderTable>
                    : isLoading ? <></> : <NoOrders />
                }
            </OrderListContainer>

            {ordersData.totalItems > ordersData.pageSize
                ? <BarekamPagination {...ordersData} onPageChange={handlePageChange} />
                : <></>
            }
        </>
    )
}

interface OrderListProps {
    columns: OrdersTableColumn[],
    ordersData: OrdersResponseModel,
    allOrdersData?: AllOrdersResponseModel,
    isWarehouseWorker?: boolean,
    openAction: (order: OrderModel, withEdit?: boolean) => void,
    onAction?: (order: OrderModel, action: InWarehouseActionTypes | ReadyForShipmentActionTypes | BulkActionPopupActionTypes) => void,
    setPaymentPopupOpened?: (isSucceeded: boolean) => void;
}

export default OrderList;
